import React, { useState, useRef, useId, useEffect } from 'react';
import { cva } from 'class-variance-authority';
import clsx from 'clsx';
import { motion, AnimatePresence } from 'motion/react';
import { duration } from './primitives/tokens';
import { useControllable } from '../../hooks/useControllable';
import styles from './AutoComplete.module.css';
import inputStyles from './Input.module.css';

const autoComplete = cva(styles.wrapper);

interface AutoCompleteOption {
  label: React.ReactNode;
  value: string;
}

interface AutoCompleteProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange' | 'onSelect'> {
  value?: string;
  defaultValue?: string;
  options?: (string | AutoCompleteOption)[];
  onChange?: (value: string) => void;
  onSearch?: (value: string) => void;
  onSelect?: (value: string) => void;
  filterOption?: boolean | ((input: string, option: AutoCompleteOption) => boolean);
  open?: boolean;
  disabled?: boolean;
  allowClear?: boolean;
  backfill?: boolean;
  /**
   * Floating label text. Sits inside the field at rest; animates upward and
   * shrinks on focus or when the field has a non-empty value. This is the
   * ONLY label/hint API — `placeholder` is intentionally removed.
   */
  label?: string;
}

/**
 * AutoComplete — input with searchable dropdown suggestions.
 *
 * | Prop | Type | Default | Description |
 * |------|------|---------|-------------|
 * | value | string | — | Controlled value |
 * | options | (string \| AutoCompleteOption)[] | — | Suggestion list |
 * | onChange | (value) => void | — | Value change |
 * | onSearch | (value) => void | — | Search input change |
 * | onSelect | (value) => void | — | Option selected |
 * | filterOption | boolean \| function | true | Auto-filter |
 * | label | string | — | Floating label |
 * | backfill | boolean | false | Fill input on arrow-key highlight |
 *
 * @example
 * <AutoComplete options={['apple', 'banana']} label="Fruit" onSearch={fetchSuggestions} />
 */
export const AutoComplete = React.forwardRef<HTMLInputElement, AutoCompleteProps>(
  (
    {
      value: controlledValue,
      defaultValue = '',
      options = [],
      onChange,
      onSearch,
      onSelect,
      filterOption = true,
      open: controlledOpen,
      label,
      disabled = false,
      allowClear: _allowClear = false,
      backfill = false,
      className,
      style,
      ...props
    },
    ref,
  ) => {
  const [value, setValue] = useControllable(controlledValue, defaultValue, onChange);
  const [focused, setFocused] = useState(false);
  const [internalOpen, setInternalOpen] = useState(false);
  const isOpen = controlledOpen ?? (internalOpen && value.length > 0);
  const [highlightIndex, setHighlightIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const generatedId = useId();
  const inputId = (props as { id?: string }).id ?? generatedId;

  // Merge external ref with internal ref
  useEffect(() => {
    if (ref) {
      if (typeof ref === 'function') {
        ref(inputRef.current);
      } else {
        (ref as React.MutableRefObject<HTMLInputElement | null>).current = inputRef.current;
      }
    }
  }, [ref]);

  const normalized: AutoCompleteOption[] = options.map((o) => (typeof o === 'string' ? { label: o, value: o } : o));

  const defaultFilter = (input: string, opt: AutoCompleteOption) =>
    String(opt.value).toLowerCase().includes(input.toLowerCase());

  const filtered =
    filterOption === false
      ? normalized
      : normalized.filter((o) =>
          typeof filterOption === 'function' ? filterOption(value, o) : defaultFilter(value, o),
        );

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    setValue(v);
    onSearch?.(v);
    setInternalOpen(true);
    setHighlightIndex(0);
  };

  const handleSelect = (optValue: string) => {
    setValue(optValue);
    onSelect?.(optValue);
    setInternalOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIndex((i) => Math.min(i + 1, filtered.length - 1));
      if (backfill && filtered[highlightIndex + 1]) setValue(filtered[highlightIndex + 1].value);
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIndex((i) => Math.max(i - 1, 0));
      if (backfill && filtered[highlightIndex - 1]) setValue(filtered[highlightIndex - 1].value);
    }
    if (e.key === 'Enter' && filtered[highlightIndex]) {
      handleSelect(filtered[highlightIndex].value);
    }
    if (e.key === 'Escape') setInternalOpen(false);
  };

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current?.contains(e.target as Node)) return;
      setInternalOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen]);

  const hasValue = value.length > 0;
  const labelFloated = focused || hasValue;

  return (
    <div ref={wrapperRef} className={autoComplete({ className })} style={style} {...props}>
      {label !== undefined && (
        <label
          htmlFor={inputId}
          className={clsx(
            inputStyles.floatingLabel,
            labelFloated ? inputStyles.labelFloated : inputStyles.labelRest,
            focused && inputStyles.labelFocused,
          )}
        >
          {label}
        </label>
      )}
      <motion.input
        ref={inputRef}
        id={inputId}
        value={value}
        onChange={handleChange}
        onFocus={() => { setFocused(true); setInternalOpen(true); }}
        onBlur={() => setFocused(false)}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        animate={{
          backgroundColor: focused ? 'var(--surface-container-lowest)' : 'var(--surface-container-high)',
        }}
        transition={{ duration: duration.normal / 1000 }}
        className={styles.input}
      />
      <AnimatePresence>
        {isOpen && filtered.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 4 }}
            transition={{ duration: duration.normal / 1000 }}
            className={styles.dropdown}
          >
            {filtered.map((opt, i) => (
              <div
                key={opt.value}
                onClick={() => handleSelect(opt.value)}
                onMouseEnter={() => setHighlightIndex(i)}
                className={clsx(styles.item, i === highlightIndex && styles.itemHighlighted)}
              >
                {opt.label}
              </div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
});

AutoComplete.displayName = 'AutoComplete';
